import resolveContext from './resolveContext';
import augment from './augment';
import {getConfigItem, configConstants} from '@servicenow/ui-config';
import {defer, whenIdle} from '@servicenow/ui-utils';
import marks from './marks';
import {increment as incrementCount} from './counters';
import shouldMark from './shouldMark';
import {types} from './constants';
import observers from './observers';
import validateMarkArguments from './validateMarkArguments';

const {
	METRICS_OBSERVER_INTERVAL,
	METRICS_USE_WINDOW_PERFORMANCE,
	METRICS_MARK_PRUNING_INTERVAL,
	METRICS_MARK_EXPIRATION,
	METRICS_PRUNE_MARKS
} = configConstants;
let timeoutId;
const latest = [];
const MARK_PRUNING_INTERVAL = getConfigItem(METRICS_MARK_PRUNING_INTERVAL);
const MARK_EXPIRATION = getConfigItem(METRICS_MARK_EXPIRATION);
const PRUNE_MARKS = getConfigItem(METRICS_PRUNE_MARKS);

let {performance} = window;
if (!getConfigItem(METRICS_USE_WINDOW_PERFORMANCE)) {
	try {
		performance = window.top.performance; // eslint-disable-line prefer-destructuring
	} catch (e) {
		/* catch if !window.top */
	}
}

const callObservers = () => {
	if (observers.size) {
		clearTimeout(timeoutId);
		timeoutId = defer(() => {
			whenIdle(() => {
				const latestSlice = latest.splice(0);
				if (latestSlice.length) observers.forEach(observer => observer([...latestSlice]));
			});
		}, getConfigItem(METRICS_OBSERVER_INTERVAL));
	}
};

const unresolvedResourceMarks = [];

const isMatch = (mark, resource) => {
	if (isMarkBatchedRequest(mark) && isResourceBatchApi(resource)) return true;

	if (resource.name.includes(mark?.options?.path)) return true;

	return false;
};

const isResourceBatchApi = resource => resource.name.includes('/api/now/v1/batch');
const isMarkBatchedRequest = mark => mark?.options?.batch;

// not currently supported in component unit testing environment
if (window.PerformanceObserver) {
	const observer = new PerformanceObserver(list => {
		const resources = list.getEntries().sort((a, b) => {
			if (a.startTime < b.startTime) return -1;
			if (a.startTime > b.startTime) return 1;

			return 0;
		});

		for (let m = 0; m < unresolvedResourceMarks.length; m++) {
			const mark = unresolvedResourceMarks[m];

			for (let e = 0; e < resources.length; e++) {
				const resource = resources[e];

				if (resource.startTime > mark.markTime && isMatch(mark, resource)) {
					mark.options.resource = resource.toJSON();
					unresolvedResourceMarks.splice(m, 1);
					m -= 1;
					latest.push(mark);
					callObservers();
					if (!isMarkBatchedRequest(mark)) resources.splice(e, 1);
					break;
				}
			}
		}
	});
	observer.observe({
		entryTypes: ['resource']
	});
}

if (PRUNE_MARKS && MARK_PRUNING_INTERVAL > 0) {
	setInterval(() => {
		const now = performance.now();

		for (let i = 0; i < unresolvedResourceMarks.length; i++) {
			if (now - unresolvedResourceMarks[i].markTime >= MARK_EXPIRATION) {
				if (process.env.NODE_ENV !== 'production') {
					const count = unresolvedResourceMarks.length - i;
					console.warn(
						`@servicenow/ui-metrics Pruning ${count} unresolved resource mark${
							count > 1 ? 's' : ''
						}.`
					); // eslint-disable-line no-console
				}
				unresolvedResourceMarks.splice(i);
				break;
			}
		}

		for (let i = 0; i < marks.length; i++) {
			if (now - marks[i].markTime >= MARK_EXPIRATION) {
				if (process.env.NODE_ENV !== 'production') {
					const count = marks.length - i;
					console.info(
						`@servicenow/ui-metrics Pruning ${marks.length - i} expired mark${
							count > 1 ? 's' : ''
						}.`
					); // eslint-disable-line no-console
				}
				marks.splice(i);
				break;
			}
		}
	}, MARK_PRUNING_INTERVAL);
}

/**
 * Marks a Seismic friendly performance marker
 * @function mark
 * @example
 * import {mark} from '@servicenow/ui-metrics';
 *
 * actionHandlers: {
 * 		[COMPONENT_TREE_RENDERED]({host, action: {meta}}) {
 *			if (hasFetched && !loading && !interactive) {
 *
 * 				// Mark a custom defined metric and flip interactive
 * 				// Initial interactive state is false
 *				mark(host, getInteractionId(meta), metrics.HERO_INTERACTIVE);
 *				updateState({
 *					shouldRender: false,
 *					path: 'interactive',
 *					value: true,
 *					operation: 'set'
 *				});
 *			}
 *		}
 * }
 *
 * @param {Object} context - (Optional) Context object to attach mark to
 * @param {String} interactionId - The id of the action that trigger the mark. Helps track client transactions during the interaction
 * @param {String} event - The metric type you are marking (custom metrics must be defined first).
 * @param {Object} options - (Optional) Typically this is the label or type of the mark.  Labels should be `:` delimited.
 * @param {String} type - (Optional) The category of metric.  Defaults to `performance`.  Other common options are `usage` || `both`.
 */
export default function mark(
	context = {},
	interactionId,
	event,
	options = {},
	type = 'performance'
) {
	incrementCount(event);
	const resolvedContext = resolveContext(context);
	if (!shouldMark(resolvedContext, event)) return;

	validateMarkArguments(resolvedContext, interactionId, event, options, type);
	const {componentName, componentId, parentComponentId} = resolvedContext;
	const mark = {
		componentName,
		componentId,
		parentComponentId,
		markTime: performance.now(),
		interactionId,
		event,
		options,
		type
	};
	const augmentedMark = augment(mark);

	if (event === types.HTTP || event === types.GQL) {
		unresolvedResourceMarks.push(augmentedMark);
		augmentedMark.options.resource = {
			unresolved: true
		};
	}

	marks.push(augmentedMark);

	if (observers.size && !augmentedMark?.options?.resource?.unresolved) {
		latest.push(augmentedMark);
		callObservers();
	}
}
